import { apiClient } from "@/lib/api";
import type {
  Booking,
  BookingAttentionSummary,
  BookingCardPage,
  BookingDaySummary,
  BookingFacetCounts,
  BookingListFilters,
  BookingPageParams,
  CustomerPointsSummary,
  CustomerReceiptConfirmResponse,
  CustomerReceiptUploadResponse,
  ReceiptAnalyzeResponse,
  ReceiptCaptureResponse,
} from "@/types";
import type { AdminBookingDetailResponse } from "./adminBookingDetail";

export type RefundChoice = "DEFAULT" | "FORCE_REFUND" | "FORCE_NO_REFUND";

export type { AdminBookingDetailResponse } from "./adminBookingDetail";

/**
 * Every booking list read sits under `["bookings"]`, so mutations that
 * invalidate `["bookings"]` refresh pages, counters and day summaries together.
 */
export const bookingKeys = {
  all: ["bookings"] as const,
  page: (params: BookingPageParams) =>
    [...bookingKeys.all, "page", params] as const,
  facets: (filters: BookingListFilters) =>
    [...bookingKeys.all, "facets", filters] as const,
  attention: (filters: BookingListFilters) =>
    [...bookingKeys.all, "attention", filters] as const,
  days: (filters: BookingListFilters) =>
    [...bookingKeys.all, "days", filters] as const,
};

export const bookingService = {
  getPage: async (params: BookingPageParams): Promise<BookingCardPage> => {
    const response = await apiClient.get<BookingCardPage>("/bookings/page", {
      params,
    });
    return response.data;
  },

  getFacets: async (
    filters: BookingListFilters,
  ): Promise<BookingFacetCounts> => {
    const response = await apiClient.get<BookingFacetCounts>(
      "/bookings/facets",
      { params: filters },
    );
    return response.data;
  },

  getAttention: async (
    filters: BookingListFilters,
  ): Promise<BookingAttentionSummary> => {
    const response = await apiClient.get<BookingAttentionSummary>(
      "/bookings/attention",
      { params: filters },
    );
    return response.data;
  },

  /** Requires `visit_date_from` + `visit_date_to` (at most 62 days). */
  getDays: async (
    filters: BookingListFilters,
  ): Promise<BookingDaySummary[]> => {
    const response = await apiClient.get<BookingDaySummary[]>(
      "/bookings/days",
      { params: filters },
    );
    return response.data;
  },

  getById: async (bookingId: string): Promise<Booking> => {
    const response = await apiClient.get<Booking>(`/bookings/${bookingId}`);
    return response.data;
  },

  getAdminDetail: async (
    bookingId: string,
  ): Promise<AdminBookingDetailResponse> => {
    const response = await apiClient.get<AdminBookingDetailResponse>(
      `/admin/bookings/${bookingId}/detail`,
    );
    return response.data;
  },

  // Customer portal APIs
  getCustomerBookings: async (): Promise<{
    bookings: Booking[];
  }> => {
    const response = await apiClient.get<{ bookings: Booking[] }>(
      "/customer/bookings",
    );
    return response.data;
  },

  captureCustomerReceipt: async (
    bookingId: string,
    file: File,
  ): Promise<ReceiptCaptureResponse> => {
    const form = new FormData();
    form.append("file", file);
    const response = await apiClient.post<ReceiptCaptureResponse>(
      `/customer/bookings/${bookingId}/receipt/capture`,
      form,
    );
    return response.data;
  },

  getCustomerPointsSummary: async (): Promise<CustomerPointsSummary> => {
    const response =
      await apiClient.get<CustomerPointsSummary>("/customer/points");
    return response.data;
  },

  uploadSellerReceipt: async (
    bookingId: string,
    file: File,
  ): Promise<CustomerReceiptUploadResponse> => {
    const form = new FormData();
    form.append("file", file);
    const response = await apiClient.post<CustomerReceiptUploadResponse>(
      `/bookings/${bookingId}/receipt/upload`,
      form,
    );
    return response.data;
  },

  analyzeSellerReceipt: async (
    bookingId: string,
    storagePath: string,
  ): Promise<ReceiptAnalyzeResponse> => {
    const response = await apiClient.post<ReceiptAnalyzeResponse>(
      `/bookings/${bookingId}/receipt/analyze`,
      { storage_path: storagePath },
    );
    return response.data;
  },

  registerSellerReceipt: async (
    bookingId: string,
    storagePath: string,
    extractedTotalCents: number,
    amountSource: "OCR" | "MANUAL" = "OCR",
  ): Promise<Booking> => {
    const response = await apiClient.post<Booking>(
      `/bookings/${bookingId}/receipt/register`,
      {
        storage_path: storagePath,
        extracted_total_cents: extractedTotalCents,
        amount_source: amountSource,
      },
    );
    return response.data;
  },

  rejectSellerReceipt: async (
    bookingId: string,
    storagePath: string,
  ): Promise<ReceiptAnalyzeResponse> => {
    const response = await apiClient.post<ReceiptAnalyzeResponse>(
      `/bookings/${bookingId}/receipt/reject`,
      { storage_path: storagePath },
    );
    return response.data;
  },

  completeBooking: async (bookingId: string): Promise<Booking> => {
    const response = await apiClient.post<Booking>(
      `/bookings/${bookingId}/complete`,
    );
    return response.data;
  },

  cancelBooking: async (
    bookingId: string,
    reason?: string,
  ): Promise<Booking> => {
    const response = await apiClient.post<Booking>(
      `/bookings/${bookingId}/cancel`,
      reason != null ? { reason } : undefined,
    );
    return response.data;
  },

  cancelBookingAdmin: async (
    bookingId: string,
    body: { reason?: string; refund_choice?: RefundChoice },
  ): Promise<Booking> => {
    const response = await apiClient.post<Booking>(
      `/bookings/${bookingId}/cancel`,
      body,
    );
    return response.data;
  },

  amendBooking: async (
    bookingId: string,
    body: {
      visit_at: string;
      time_slot: string;
      override_capacity?: boolean;
    },
  ): Promise<Booking> => {
    const response = await apiClient.post<Booking>(
      `/bookings/${bookingId}/amend`,
      body,
    );
    return response.data;
  },

  adjustAdminReceiptTotal: async (
    bookingId: string,
    newTotalCents: number,
    reason?: string | null,
  ): Promise<Booking> => {
    const response = await apiClient.patch<Booking>(
      `/admin/bookings/${bookingId}/receipt-total`,
      {
        new_total_cents: newTotalCents,
        reason: reason ?? undefined,
      },
    );
    return response.data;
  },

  revealContactPhone: async (
    bookingId: string,
  ): Promise<{ contact_phone: string }> => {
    const response = await apiClient.post<{ contact_phone: string }>(
      `/admin/bookings/${bookingId}/reveal-contact-phone`,
    );
    return response.data;
  },

  updateSellerNotes: async (
    bookingId: string,
    sellerNotes: string | null,
  ): Promise<Booking> => {
    const response = await apiClient.patch<Booking>(
      `/bookings/${bookingId}/notes`,
      { seller_notes: sellerNotes?.trim() ? sellerNotes.trim() : null },
    );
    return response.data;
  },

  confirmCustomerReceipt: async (
    bookingId: string,
    storagePath: string,
    extractedTotalCents: number,
  ): Promise<CustomerReceiptConfirmResponse> => {
    const response = await apiClient.post<CustomerReceiptConfirmResponse>(
      `/customer/bookings/${bookingId}/receipt/confirm`,
      { storage_path: storagePath, extracted_total_cents: extractedTotalCents },
    );
    return response.data;
  },
};
